import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';

interface PWAFloatingBannerProps {
  className?: string;
  position?: 'top' | 'bottom' | 'top-right' | 'bottom-right';
  showDelay?: number; // Delay en ms antes de mostrar
}

export const PWAFloatingBanner: React.FC<PWAFloatingBannerProps> = ({ 
  className = '', 
  position = 'bottom-right',
  showDelay = 3000 // 3 segundos por defecto
}) => {
  const { isInstalled, isInstallable, installPWA } = usePWA();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInStandalone, setIsInStandalone] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone (PWA)
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;
      setIsInStandalone(standalone);
    };

    checkStandalone();
    window.addEventListener('resize', checkStandalone);

    // Mostrar banner solo si:
    // 1. No está instalada Y es instalable
    // 2. No está en modo standalone
    // 3. No ha sido descartada
    const shouldShow = !isInstalled && 
                      isInstallable && 
                      !isInStandalone && 
                      !isDismissed;

    if (shouldShow) {
      // Delay antes de mostrar
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, showDelay);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', checkStandalone);
      };
    }

    return () => {
      window.removeEventListener('resize', checkStandalone);
    };
  }, [isInstalled, isInstallable, isInStandalone, isDismissed, showDelay]);

  // Verificar si ya fue descartada anteriormente
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-floating-banner-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      setIsVisible(false);
    }
  }, []);

  // No mostrar si ya está en PWA o no es visible
  if (isInStandalone || !isVisible) {
    return null;
  }

  const handleInstall = async () => {
    const success = await installPWA();
    if (success) {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    
    // Guardar en localStorage para no mostrar de nuevo
    localStorage.setItem('pwa-floating-banner-dismissed', 'true');
  };

  const handleLearnMore = () => {
    // Navegar a página de información PWA
    window.location.href = '/pwa-info';
  };

  // Posicionamiento del banner
  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'top-4 left-4 right-4';
      case 'bottom':
        return 'bottom-4 left-4 right-4';
      case 'top-right':
        return 'top-4 right-4 max-w-sm';
      case 'bottom-right':
        return 'bottom-4 right-4 max-w-sm';
      default:
        return 'bottom-4 right-4 max-w-sm';
    }
  };

  return (
    <div className={`fixed ${getPositionClasses()} z-50 ${className}`}>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-in slide-in-from-bottom-2 duration-300">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900">
              Instala Servicio a Tiempo
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Accede más rápido y recibe notificaciones
            </p>
          </div>
          
          <div className="flex-shrink-0 flex space-x-1">
            <button
              onClick={handleInstall}
              className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Instalar
            </button>
            <button
              onClick={handleLearnMore}
              className="text-blue-600 hover:text-blue-800 text-xs font-medium focus:outline-none transition-colors"
            >
              Info
            </button>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Indicadores de beneficios */}
        <div className="mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-blue-600">
            <span>🚀 Más rápido</span>
            <span>📱 Como app</span>
            <span>🔔 Notificaciones</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Variantes del banner
export const PWATopBanner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <PWAFloatingBanner 
    className={className} 
    position="top" 
    showDelay={2000} 
  />
);

export const PWABottomBanner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <PWAFloatingBanner 
    className={className} 
    position="bottom" 
    showDelay={5000} 
  />
);

export const PWATopRightBanner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <PWAFloatingBanner 
    className={className} 
    position="top-right" 
    showDelay={4000} 
  />
);

export const PWABottomRightBanner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <PWAFloatingBanner 
    className={className} 
    position="bottom-right" 
    showDelay={3000} 
  />
);
